import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck } from 'lucide-react';
import { api } from '../api/client';
import { Badge, Button, EmptyState, Skeleton, cn } from './primitives';
import { DialogShell } from './confirm-dialog';
import { formatRelative } from '../lib/format';
import { t } from '../../i18n';

/**
 * The platform team's inbox — the bell in the platform shell and the
 * "Latest activity" card on the overview.
 *
 * Every row is one of the alerts `platform-alerts.ts` also emails: a plan
 * bought, a receipt to verify, a society signed up, a ticket opened. The
 * rows are written before the mail is attempted, so the console shows the
 * event even when the mail server does not.
 */

export interface PlatformInboxRow {
  id: string;
  audience: 'money' | 'support' | 'growth';
  title: string;
  body: string;
  path: string;
  societyId: string | null;
  readAt: string | null;
  createdAt: string;
}

const AUDIENCE_LABEL: Record<PlatformInboxRow['audience'], string> = { money: 'Money', support: 'Support', growth: 'Growth' };
const AUDIENCE_TONE: Record<PlatformInboxRow['audience'], 'success' | 'marigold' | 'indigo'> = { money: 'success', support: 'marigold', growth: 'indigo' };

/** Polled every minute so the badge stays current without a socket. */
export function usePlatformInboxUnread(): number {
  const query = useQuery({
    queryKey: ['platform-inbox', 'unread-count'],
    queryFn: () => api.get<{ unreadCount: number }>('/api/v1/platform/inbox/unread-count'),
    refetchInterval: 60_000,
  });
  return query.data?.unreadCount ?? 0;
}

export function usePlatformInbox(limit = 30, enabled = true) {
  return useQuery({
    queryKey: ['platform-inbox', 'list', limit],
    queryFn: () => api.getPage<PlatformInboxRow>('/api/v1/platform/inbox', { query: { limit } }),
    enabled,
    refetchInterval: 60_000,
  });
}

export function usePlatformInboxActions() {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['platform-inbox'] });
  const markRead = useMutation({
    mutationFn: (id: string) => api.post(`/api/v1/platform/inbox/${id}/read`, {}),
    onSuccess: () => void invalidate(),
  });
  const markAllRead = useMutation({
    mutationFn: () => api.post('/api/v1/platform/inbox/read-all', {}),
    onSuccess: () => void invalidate(),
  });
  return { markRead, markAllRead };
}

/** One row, shared by the bell's list and the overview card. */
export function PlatformInboxItem({ row, onOpen }: { row: PlatformInboxRow; onOpen: (row: PlatformInboxRow) => void }): React.JSX.Element {
  return (
    <li>
      <button
        type="button"
        onClick={() => onOpen(row)}
        aria-label={t('{0}. Open.', [row.title])}
        className="-mx-1 flex w-full gap-3 rounded-sk-sm px-1 py-3 text-left hover:bg-surface-variant focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo/50"
      >
        {/* An unread dot plus the word "New" — colour alone cannot carry the state. */}
        <span aria-hidden className={cn('mt-1.5 size-2 shrink-0 rounded-full', row.readAt ? 'bg-transparent' : 'bg-indigo')} />
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-baseline gap-x-2">
            <span className="text-[13px] font-semibold text-ink-1">{row.title}</span>
            {!row.readAt ? <Badge tone="indigo">{t('New')}</Badge> : null}
            <Badge tone={AUDIENCE_TONE[row.audience]}>{AUDIENCE_LABEL[row.audience]}</Badge>
          </span>
          <span className="mt-0.5 block text-[13px] text-ink-2">{row.body}</span>
          <span className="mt-0.5 block text-2xs text-ink-3">{formatRelative(row.createdAt)}</span>
        </span>
      </button>
    </li>
  );
}

export function PlatformInboxPanel({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }): React.JSX.Element {
  const navigate = useNavigate();
  const inbox = usePlatformInbox(30, open);
  const { markRead, markAllRead } = usePlatformInboxActions();
  const rows = inbox.data?.items ?? [];
  const unread = rows.filter((row) => !row.readAt).length;

  const openRow = (row: PlatformInboxRow): void => {
    if (!row.readAt) markRead.mutate(row.id);
    onOpenChange(false);
    navigate(row.path);
  };

  return (
    <DialogShell
      open={open}
      onOpenChange={onOpenChange}
      title={t('Notifications')}
      description={unread > 0 ? `${unread} unread` : t('Everything here has been read.')}
      className="max-w-lg"
      footer={
        <Button variant="secondary" onClick={() => markAllRead.mutate()} loading={markAllRead.isPending} disabled={unread === 0}>
          <CheckCheck className="size-4" aria-hidden />{' '}{t('Mark all as read')}
        </Button>
      }
    >
      {inbox.isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }, (_, index) => (
            <Skeleton key={index} className="h-16" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<Bell className="size-5" />}
          title={t('Nothing yet')}
          description={t('Purchases, receipts to verify, new societies and support tickets show up here as they happen — and by email.')}
        />
      ) : (
        <ul className="divide-y divide-outline-soft">
          {rows.map((row) => (
            <PlatformInboxItem key={row.id} row={row} onOpen={openRow} />
          ))}
        </ul>
      )}
    </DialogShell>
  );
}
